import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, ShieldAlert, TrendingUp, PlayCircle, ChevronRight,
  LayoutDashboard, Users, FileText,
} from "lucide-react";
import JeaButton from "@/components/jea/JeaButton";
import JeaCard from "@/components/jea/JeaCard";
import { useDemo } from "@/pages/demo/DemoShell";

const ValueProp = ({ Icon, title, body, color }) => (
  <JeaCard>
    <div className="flex items-start gap-3">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
        style={{ background: `${color}22`, border: `1px solid ${color}55`, color }}
      >
        <Icon size={18} />
      </div>
      <div>
        <h3 className="text-white font-bold text-[15px]">{title}</h3>
        <p className="text-text-secondary text-[13px] mt-1">{body}</p>
      </div>
    </div>
  </JeaCard>
);

const ExploreTile = ({ to, Icon, title, body }) => (
  <Link to={to} className="block" data-testid={`demo-explore-${to.replace(/\//g, "-")}`}>
    <JeaCard className="h-full hover:border-accent-pink/60 transition-colors">
      <div className="flex items-center gap-3 mb-1">
        <Icon size={18} className="text-accent-pink" />
        <h3 className="text-white font-bold text-[15px]">{title}</h3>
      </div>
      <p className="text-text-secondary text-[13px]">{body}</p>
    </JeaCard>
  </Link>
);

const DemoLandingPage = () => {
  const nav = useNavigate();
  const demo = useDemo();
  return (
    <div className="space-y-8" data-testid="demo-landing-page">
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      >
        <JeaCard className="p-12" style={{ boxShadow: "0 0 0 1px rgba(251, 111, 146, 0.20), 0 4px 24px rgba(0,0,0,0.40)" }}>
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8 items-center">
            <div>
              <h1 className="text-white font-extrabold tracking-[-0.02em] text-[40px]">
                Try Judgment Enforcement <span className="text-accent-pink">AI</span>
              </h1>
              <p className="text-text-secondary text-[16px] mt-4 max-w-[560px]">
                See how attorneys and collection firms recover more with AI-powered asset discovery
                across 7 data brokers, bankruptcy-safe enforcement, and portfolio-level monitoring.
              </p>
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <JeaButton onClick={demo?.startTour} className="gap-2" data-testid="demo-start-tour">
                  <PlayCircle size={16} /> Start the 90-second Tour
                </JeaButton>
                <button
                  type="button"
                  onClick={() => nav("/demo/dashboard")}
                  className="text-accent-pink hover:text-accent-pink-light text-[14px] font-semibold flex items-center gap-1"
                  data-testid="demo-skip-tour"
                >
                  Skip to the app <ChevronRight size={14} />
                </button>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="aspect-square rounded-2xl bg-bg-tertiary flex items-center justify-center" data-testid="demo-hero-illustration">
                <div className="text-center px-6">
                  <TrendingUp size={64} className="text-accent-pink mx-auto" />
                  <div className="mt-3 text-white font-bold text-[14px]">15 judgments monitored</div>
                  <div className="jea-mono text-accent-pink text-[24px] font-extrabold">$97.3M</div>
                  <div className="text-text-muted text-[11px] mt-1">Portfolio value + accrued interest</div>
                </div>
              </div>
            </div>
          </div>
        </JeaCard>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ValueProp Icon={Search} title="7 Data Brokers, One Query" color="#fb6f92"
          body="Accurint, TLOxp, Work Number, PACER, CourtListener, Docket Alarm, UniCourt — orchestrated in a single sweep." />
        <ValueProp Icon={ShieldAlert} title="Bankruptcy-Safe Enforcement" color="#F39C12"
          body="Active § 362 stays flip your recommendations to HALT automatically. No accidental stay violations." />
        <ValueProp Icon={TrendingUp} title="Recover More Judgments" color="#2ECC71"
          body="Lookback searches, monitoring, and interest accrual keep every dollar in play." />
      </div>

      <div>
        <h2 className="text-white font-bold text-[18px] mb-3">Explore the Demo</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <ExploreTile to="/demo/dashboard" Icon={LayoutDashboard} title="Dashboard"
            body="PRO command center with 15 seeded judgments + stats + activity feed." />
          <ExploreTile to="/demo/debtors" Icon={Users} title="Judgments"
            body="Sortable, filterable table of every judgment in the portfolio." />
          <ExploreTile to="/demo/bankruptcy-watch" Icon={ShieldAlert} title="Bankruptcy Watch"
            body="Two active filings surfaced from PACER. Automatic stay explainer included." />
          <ExploreTile to="/demo/reports" Icon={FileText} title="Reports"
            body="Sample reports — asset discovery, change logs, enforcement recommendations." />
        </div>
      </div>

      <JeaCard className="text-center">
        <h2 className="text-white font-bold text-[22px]">Ready to get started?</h2>
        <p className="text-text-secondary text-[14px] mt-2">
          Sign up in 2 minutes. Standard tier is free to try — no card required.
        </p>
        <div className="mt-4 flex justify-center">
          <JeaButton size="lg" onClick={() => nav("/register")} className="gap-2" data-testid="demo-footer-signup">
            Sign Up Free
          </JeaButton>
        </div>
      </JeaCard>
    </div>
  );
};

export default DemoLandingPage;
